/**
 * Pure rectangle / point queries over map data (world pixels). Phaser-free so unit tests and tools
 * can use them. All rectangles are half-open: a point at `x + w` is outside.
 */
import type { MapExit, MapInteraction } from '@/core/types/contracts';

export interface Rect { x: number; y: number; w: number; h: number }

export function rectContains(r: Rect, px: number, py: number): boolean {
  return px >= r.x && px < r.x + r.w && py >= r.y && py < r.y + r.h;
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

export function rectCenter(r: Rect): { x: number; y: number } {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}

/** Squared distance from a point to the centre of a rect (used for deterministic tie-breaks). */
export function distSqToRectCenter(r: Rect, px: number, py: number): number {
  const c = rectCenter(r);
  const dx = c.x - px, dy = c.y - py;
  return dx * dx + dy * dy;
}

/** First rect (in data order) containing the point. */
export function firstRectAt<T extends Rect>(list: readonly T[], px: number, py: number): T | null {
  for (const r of list) if (rectContains(r, px, py)) return r;
  return null;
}

/** Every rect containing the point, in data order. */
export function rectsAt<T extends Rect>(list: readonly T[], px: number, py: number): T[] {
  return list.filter((r) => rectContains(r, px, py));
}

export function exitAt(exits: readonly MapExit[], px: number, py: number): MapExit | null {
  return firstRectAt(exits, px, py);
}

export function interactionsAt(list: readonly MapInteraction[], px: number, py: number): MapInteraction[] {
  return rectsAt(list, px, py);
}

/** Converts a tile-space rect to world pixels. */
export function tileRect(tx: number, ty: number, tw: number, th: number, tileSize: number): Rect {
  return { x: tx * tileSize, y: ty * tileSize, w: tw * tileSize, h: th * tileSize };
}
